import type { User, UserDataResponse } from "../Interfaces/LoginInterface";
import type { ApiUserLookup } from "../Apis/users";
import type { ApiUser } from "../Interfaces/UsersInterface";

export const PERMISSION_GROUP_NOT_CONFIGURED_MESSAGE =
  "Your account is not fully configured. Contact your administrator.";

export const TL_NO_TEAM_MEMBERS_MESSAGE =
  "No team members assigned to your account yet.";

export const PERMISSION_GROUP_ERROR_CODES = [
  "PERMISSION_GROUP_NOT_ASSIGNED",
  "PERMISSION_GROUP_INVALID",
] as const;

export function isPermissionGroupContextError(error: unknown): boolean {
  const code = extractMeContextErrorCode(error);
  return (
    code != null &&
    (PERMISSION_GROUP_ERROR_CODES as readonly string[]).includes(code)
  );
}

export function extractMeContextErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return undefined;
  }
  const data = (error as { response?: { data?: { error?: string; message?: string } } })
    .response?.data;
  if (typeof data?.error === "string" && data.error.trim()) {
    return data.error.trim();
  }
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  return undefined;
}

export interface TeamLeaderAccessContext {
  currentUserId: string | undefined;
  isTL: boolean;
  hasTeam: boolean;
  teamAgentIds: Set<string>;
  showNoTeamMembersBanner: boolean;
  canConfigureTeams: boolean;
  canActOn: (assignedToUserId: string | undefined | null) => boolean;
  canActOnLead: (lead: {
    assignedToUserId?: string | null;
    ownerUserId?: string | null;
  }) => boolean;
  canActOnTask: (task: {
    assignedTo?: string | null;
    assignedToUserId?: string | null;
  }) => boolean;
}

export function getTeamLeaderAccessContext(
  userProfile: UserDataResponse | null | undefined
): TeamLeaderAccessContext {
  const currentUserId = userProfile?.user?.id;
  const roles = userProfile?.user?.roles ?? [];
  const isTL = Array.isArray(roles) && roles.includes("TEAM_LEADER");
  const visibility = userProfile?.visibility;
  const userIds = visibility?.userIds ?? [];
  const hasTeam =
    visibility?.level === "TEAM" && userIds.length > 0;
  const agentIdList =
    userProfile?.team?.agentIds?.length
      ? userProfile.team.agentIds
      : userIds;
  const teamAgentIds = new Set(agentIdList.filter(Boolean));

  const canActOn = (assignedToUserId: string | undefined | null): boolean => {
    const id = assignedToUserId?.trim();
    if (!id || !currentUserId) return false;
    if (id === currentUserId) return true;
    return isTL && hasTeam && teamAgentIds.has(id);
  };

  const canActOnLead: TeamLeaderAccessContext["canActOnLead"] = (lead) =>
    canActOn(lead.assignedToUserId ?? lead.ownerUserId ?? null);

  const canActOnTask: TeamLeaderAccessContext["canActOnTask"] = (task) =>
    canActOn(task.assignedTo ?? task.assignedToUserId ?? null);

  return {
    currentUserId,
    isTL,
    hasTeam,
    teamAgentIds,
    showNoTeamMembersBanner: isTL && !hasTeam,
    canConfigureTeams: userProfile?.capabilities?.canConfigureTeams === true,
    canActOn,
    canActOnLead,
    canActOnTask,
  };
}

const BLOCKED_REASSIGN_ROLES = new Set([
  "TEAM_LEADER",
  "TENANT_ADMIN",
  "FACILITY_ADMIN",
]);

const ADMIN_ROLES = new Set(["TENANT_ADMIN", "FACILITY_ADMIN", "SUPER_ADMIN"]);

/** True when user is AGENT without TL, admin, or SPOC roles. */
export function isAgentOnlyRole(roles: string[]): boolean {
  if (!roles.includes("AGENT")) return false;
  if (roles.includes("TEAM_LEADER")) return false;
  if (roles.includes("SPOC")) return false;
  return !roles.some((r) => ADMIN_ROLES.has(r));
}

export function assigneeLabelFromLookupUser(u: ApiUserLookup): string {
  return (
    u.representative?.fullName?.trim() ||
    u.email?.trim() ||
    u._id?.trim() ||
    ""
  );
}

export function assigneeLabelFromProfileUser(user: User): string {
  const fullName = user.representative?.fullName?.trim();
  if (fullName) return fullName;
  const email = user.email;
  if (typeof email === "string") return email.trim();
  return email?.value?.trim() || user.id;
}

export interface AssigneeFilterScope {
  roles: string[];
  currentUserId?: string;
  isTL: boolean;
  hasTeam: boolean;
  teamAgentIds: Set<string>;
}

function isTeamLeaderAssigneeFilter(scope: AssigneeFilterScope): boolean {
  return scope.isTL;
}

/** Filter lookup users for My Leads assignee dropdown by role. */
export function filterAssigneeLookupForMyLeadsFilter(
  items: ApiUserLookup[],
  scope: AssigneeFilterScope
): ApiUserLookup[] {
  if (isTeamLeaderAssigneeFilter(scope)) {
    if (!scope.hasTeam) return [];
    return items.filter((user) => {
      if (user.isActive === false) return false;
      const roles = user.roles ?? [];
      if (roles.some((r) => BLOCKED_REASSIGN_ROLES.has(r))) return false;
      const id = user._id?.trim();
      if (!id || !scope.teamAgentIds.has(id)) return false;
      return roles.includes("AGENT");
    });
  }
  if (isAgentOnlyRole(scope.roles)) {
    const selfId = scope.currentUserId?.trim();
    if (!selfId) return [];
    return items.filter((u) => u._id === selfId);
  }
  return items;
}

/** Restrict assignee IDs resolved from saved views / Redux to role-allowed targets. */
export function filterAssigneeIdsForMyLeadsFilter(
  ids: string[],
  scope: AssigneeFilterScope
): string[] {
  if (isTeamLeaderAssigneeFilter(scope)) {
    if (!scope.hasTeam) return [];
    return ids.filter((id) => scope.teamAgentIds.has(id));
  }
  if (isAgentOnlyRole(scope.roles)) {
    const selfId = scope.currentUserId?.trim();
    if (!selfId) return [];
    return ids.filter((id) => id === selfId);
  }
  return ids;
}

export function toAssigneeFilterOptions(
  users: ApiUserLookup[]
): { id: string; label: string }[] {
  return users
    .filter((u) => u._id?.trim())
    .map((u) => ({
      id: u._id,
      label: assigneeLabelFromLookupUser(u) || u._id,
    }));
}

/** Reassign picker options for TL: team agents only, never TL/admin targets. */
export function filterReassignTargetsForTeamLeader(
  users: ApiUser[],
  teamAgentIds: Set<string>
): ApiUser[] {
  return users.filter((user) => {
    if (user.isActive === false) return false;
    const roles = user.roles ?? [];
    if (roles.some((r) => BLOCKED_REASSIGN_ROLES.has(r))) return false;
    const id = user._id?.trim();
    if (!id || !teamAgentIds.has(id)) return false;
    return roles.includes("AGENT");
  });
}

export function canSeeConfigurationNav(
  userProfile: UserDataResponse | null | undefined
): boolean {
  const roles = userProfile?.user?.roles ?? [];
  const isAdmin = roles.some(
    (r) => r === "TENANT_ADMIN" || r === "FACILITY_ADMIN"
  );
  if (isAdmin) return true;
  return userProfile?.capabilities?.canConfigureTeams === true;
}
